import React, { useState } from "react"
import { createPortal } from "react-dom"
import { Dropdown } from "antd"
import type { TraceTarget } from "./useTileTrace"

export const TRACE_MENU_LABEL = "Where this number comes from"

/**
 * What follows `prefix` in the test id of the chart mark the event is on
 * (Nivo ids its bars "bar.item.<key>.<index>", its slices "arc.<id>" and its
 * points "line.point.<series>.<index>"), or null off a mark.
 */
export const markId = (event: React.SyntheticEvent, prefix: string): string | null => {
  const target = event.target as Element
  const mark = target.closest?.(`[data-testid^="${prefix}"]`)
  return mark?.getAttribute("data-testid")?.slice(prefix.length) ?? null
}

/** `id` split at its last dot: a key that may hold dots, then an index. */
export const splitIndex = (id: string): [string, number] => {
  const dot = id.lastIndexOf(".")
  return [id.slice(0, dot), Number(id.slice(dot + 1))]
}

/**
 * The menu a right-click on a chart's bar, slice or point opens, with one
 * item that opens the trace of that number. The chart reads the number
 * under the event and passes it to `openMenu`; off a mark it passes null
 * and the browser's own menu shows. The context menu key on a focused
 * mark fires the same event with no pointer position: the menu then opens
 * under the mark. The menu's
 * anchor sits on the page itself, as the dashboard grid moves its tiles
 * with transforms that would carry a fixed position along.
 */
export const useChartTraceMenu = (
  openTrace: (target: TraceTarget) => void
): {
  openMenu: (event: React.MouseEvent, target: TraceTarget | null) => void
  menu: React.ReactNode
} => {
  const [at, setAt] = useState<{
    target: TraceTarget
    x: number
    y: number
  } | null>(null)

  const openMenu = (event: React.MouseEvent, target: TraceTarget | null) => {
    if (!target) return
    event.preventDefault()
    if (event.clientX === 0 && event.clientY === 0) {
      const box = (event.target as Element).getBoundingClientRect()
      setAt({ target, x: box.left, y: box.bottom })
    } else {
      setAt({ target, x: event.clientX, y: event.clientY })
    }
  }

  const menu =
    at &&
    createPortal(
      <Dropdown
        open
        autoFocus
        trigger={["contextMenu"]}
        placement="bottomLeft"
        onOpenChange={(open) => {
          if (!open) setAt(null)
        }}
        menu={{
          items: [{ key: "trace", label: TRACE_MENU_LABEL }],
          onClick: ({ domEvent }) => {
            // The menu sits in the tile's tree: keep the click off the tile.
            domEvent.stopPropagation()
            setAt(null)
            openTrace(at.target)
          },
        }}
      >
        <span
          style={{
            position: "fixed",
            left: at.x,
            top: at.y,
            width: 1,
            height: 1,
            pointerEvents: "none",
          }}
          aria-hidden
        />
      </Dropdown>,
      document.body
    )

  return { openMenu, menu }
}
